import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { noticeWindowPrefix, type ChatNotice, type NoticeKind } from '@lemontaigu/chat';
import { getReceptionStore } from './data/store.js';
import {
  groupNotices,
  isoToLocalInput,
  localInputToIso,
  needsTranslation,
  type NoticeState,
} from './noticesLogic.js';

// « Infos & annonces » (guest chat v2, Increment C7) — the dynamic knowledge
// tier's staff surface. Notices (banner + AI context, optional validity
// window) and learned facts (AI-only) take effect on the NEXT guest message,
// no deploy. Reads are direct under staff RLS; saves go through
// /api/chat/notice so translation happens on save (v2 brief §2.3).

const STATE_LABELS: Record<NoticeState, string> = {
  active: 'En cours',
  upcoming: 'À venir',
  expired: 'Expirée',
};

interface EditorState {
  id?: string;
  kind: NoticeKind;
  bodyFr: string;
  showBanner: boolean;
  startsLocal: string;
  endsLocal: string;
}

function emptyEditor(kind: NoticeKind): EditorState {
  return { kind, bodyFr: '', showBanner: kind === 'notice', startsLocal: '', endsLocal: '' };
}

function editorFrom(notice: ChatNotice): EditorState {
  return {
    id: notice.id,
    kind: notice.kind,
    bodyFr: notice.bodyFr,
    showBanner: notice.showBanner,
    startsLocal: isoToLocalInput(notice.startsAt),
    endsLocal: isoToLocalInput(notice.endsAt),
  };
}

export function Notices() {
  const store = getReceptionStore();
  const [notices, setNotices] = useState<ChatNotice[] | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [frOnlySaved, setFrOnlySaved] = useState(false);
  const [showFacts, setShowFacts] = useState(false);

  const reload = useCallback(async () => {
    try {
      setNotices(await store.listNotices());
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'chargement impossible');
    }
  }, [store]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function save(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!editor || busy) return;
    const bodyFr = editor.bodyFr.trim();
    if (!bodyFr) return;
    setBusy(true);
    setError('');
    setFrOnlySaved(false);
    try {
      const { translated } = await store.saveNotice({
        id: editor.id,
        kind: editor.kind,
        bodyFr,
        showBanner: editor.kind === 'notice' && editor.showBanner,
        startsAt: editor.kind === 'notice' ? localInputToIso(editor.startsLocal) : null,
        endsAt: editor.kind === 'notice' ? localInputToIso(editor.endsLocal) : null,
      });
      if (!translated) setFrOnlySaved(true);
      setEditor(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'enregistrement impossible');
    } finally {
      setBusy(false);
    }
  }

  async function archive(notice: ChatNotice): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await store.archiveNotice(notice.id);
      if (editor?.id === notice.id) setEditor(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'archivage impossible');
    } finally {
      setBusy(false);
    }
  }

  /** Re-save unchanged → the edge function re-attempts the EN/ES/DE translation. */
  async function retranslate(notice: ChatNotice): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const { translated } = await store.saveNotice({
        id: notice.id,
        kind: notice.kind,
        bodyFr: notice.bodyFr,
        showBanner: notice.showBanner,
        startsAt: notice.startsAt,
        endsAt: notice.endsAt,
      });
      setFrOnlySaved(!translated);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'traduction impossible');
    } finally {
      setBusy(false);
    }
  }

  const grouped = notices ? groupNotices(notices, new Date().toISOString()) : null;

  const noticeCard = (notice: ChatNotice, state?: NoticeState) => (
    <li key={notice.id} className={`notice-card ${state ? `is-${state}` : ''}`}>
      <div className="notice-card-head">
        {state && <span className={`notice-state is-${state}`}>{STATE_LABELS[state]}</span>}
        {notice.showBanner && <span className="notice-banner-badge">Bannière client</span>}
        <span className="notice-actions">
          <button disabled={busy} onClick={() => setEditor(editorFrom(notice))}>
            Modifier
          </button>
          <button disabled={busy} onClick={() => void archive(notice)}>
            Archiver
          </button>
        </span>
      </div>
      <p className="notice-body">
        {noticeWindowPrefix(notice)}
        {notice.bodyFr}
      </p>
      {needsTranslation(notice) && (
        <p className="notice-warning">
          Traduction manquante — les clients voient le texte français.{' '}
          <button disabled={busy} onClick={() => void retranslate(notice)}>
            Retraduire
          </button>
        </p>
      )}
    </li>
  );

  return (
    <div className="notices">
      <header className="notices-header">
        <div>
          <h2>Infos &amp; annonces</h2>
          <p className="muted">
            Prises en compte par l’assistant dès le prochain message — aucune mise en production.
          </p>
        </div>
        <div className="notices-new">
          <button disabled={busy} onClick={() => setEditor(emptyEditor('notice'))}>
            + Annonce
          </button>
          <button disabled={busy} onClick={() => setEditor(emptyEditor('fact'))}>
            + Info permanente (IA)
          </button>
        </div>
      </header>

      {error && <p className="notices-error">{error}</p>}
      {frOnlySaved && (
        <p className="notice-warning">
          Enregistré en français uniquement (traduction indisponible) — utilisez « Retraduire »
          plus tard.
        </p>
      )}

      {editor && (
        <form className="notice-editor" onSubmit={(e) => void save(e)}>
          <h3>
            {editor.id ? 'Modifier' : 'Nouvelle'}{' '}
            {editor.kind === 'notice' ? 'annonce' : 'information permanente (IA uniquement)'}
          </h3>
          <label>
            Texte (français)
            <textarea
              value={editor.bodyFr}
              onChange={(e) => setEditor({ ...editor, bodyFr: e.target.value })}
              rows={3}
              maxLength={1000}
              placeholder={
                editor.kind === 'notice'
                  ? 'Ex. : Le restaurant est fermé ce soir.'
                  : 'Ex. : Le code du local à vélos est 1234.'
              }
              autoFocus
            />
          </label>
          {editor.kind === 'notice' && (
            <>
              <label className="notice-editor-check">
                <input
                  type="checkbox"
                  checked={editor.showBanner}
                  onChange={(e) => setEditor({ ...editor, showBanner: e.target.checked })}
                />
                Afficher aux clients (bannière en haut du chat, traduite automatiquement)
              </label>
              <div className="notice-editor-window">
                <label>
                  Début (optionnel)
                  <input
                    type="datetime-local"
                    value={editor.startsLocal}
                    onChange={(e) => setEditor({ ...editor, startsLocal: e.target.value })}
                  />
                </label>
                <label>
                  Fin (optionnel)
                  <input
                    type="datetime-local"
                    value={editor.endsLocal}
                    onChange={(e) => setEditor({ ...editor, endsLocal: e.target.value })}
                  />
                </label>
              </div>
            </>
          )}
          <div className="notice-editor-actions">
            <button type="submit" disabled={busy || editor.bodyFr.trim() === ''}>
              Enregistrer
            </button>
            <button type="button" disabled={busy} onClick={() => setEditor(null)}>
              Annuler
            </button>
          </div>
        </form>
      )}

      {!grouped && !error && <p className="muted">Chargement…</p>}

      {grouped && (
        <>
          {grouped.notices.length === 0 && (
            <p className="muted">Aucune annonce en cours.</p>
          )}
          <ul className="notice-list">
            {grouped.notices.map(({ notice, state }) => noticeCard(notice, state))}
          </ul>

          <section className="notice-facts">
            <button className="notice-facts-toggle" onClick={() => setShowFacts(!showFacts)}>
              {showFacts ? '▾' : '▸'} Base apprise — IA uniquement ({grouped.facts.length})
            </button>
            {showFacts && grouped.facts.length === 0 && (
              <p className="muted">Aucune information apprise pour l’instant.</p>
            )}
            {showFacts && <ul className="notice-list">{grouped.facts.map((n) => noticeCard(n))}</ul>}
          </section>
        </>
      )}
    </div>
  );
}
